import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";

interface SearchResult {
  title?: string;
  content?: string;
  date?: string;
  source?: string;
  relevance?: number;
  similarity?: number;
  url?: string;
  summary?: string;
  tags?: string[];
}

interface SearchResultCardProps {
  result: SearchResult;
}

const SearchResultCard: React.FC<SearchResultCardProps> = ({ result = {} }) => {
  const [showTags, setShowTags] = useState(false);
  const [expandSummary, setExpandSummary] = useState(false);

  const {
    title = "Untitled",
    date,
    source = "Unknown source",
    relevance = 0,
    url,
    summary,
    tags = [],
  } = result;

  const formattedDate = date ? new Date(date).toLocaleDateString() : "No date";
  const formattedRelevance = Number(relevance).toFixed(2);

  const getRandomBgColor = () => {
    const colors = [
      "bg-pink-100 text-pink-800 border-pink-300",
      "bg-indigo-100 text-indigo-800 border-indigo-300",
      "bg-cyan-100 text-cyan-800 border-cyan-300",
      "bg-green-100 text-green-800 border-green-300",
      "bg-yellow-100 text-yellow-800 border-yellow-300",
      "bg-orange-100 text-orange-800 border-orange-300",
      "bg-purple-100 text-purple-800 border-purple-300",
      "bg-lime-100 text-lime-800 border-lime-300",
      "bg-amber-100 text-amber-800 border-amber-300",
      "bg-teal-100 text-teal-800 border-teal-300",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  return (
    <Card
      className="cursor-pointer transition-transform duration-200 rounded-xl shadow-sm hover:shadow-md
 hover:scale-[1.01] bg-white"
      onClick={() => url && window.open(url, "_blank", "noopener noreferrer")}
    >
      <CardContent className="p-5 space-y-3">
        <div className="flex justify-between items-center text-xs text-gray-500 font-medium">
          <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border border-gray-300 bg-gray-100 text-gray-700 rounded">
            {formattedRelevance}% relevant
          </span>
          <time dateTime={date}>{formattedDate}</time>
        </div>

        <h3 className="text-lg font-semibold text-black tracking-tight">
          {title}
        </h3>

        {summary && (
          <div className="relative text-sm text-gray-700 leading-relaxed">
            <p className={expandSummary ? "" : "line-clamp-4"}>{summary}</p>
            {summary.length > 100 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandSummary(!expandSummary);
                }}
                className="absolute bottom-0 right-0 text-xs font-medium text-blue-600 hover:underline bg-white px-2 py-1"
              >
                {expandSummary ? "Collapse" : "Expand"}
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span
            className={`px-2 py-0.5 border rounded-full ${getRandomBgColor()}`}
          >
            {source}
          </span>

          {tags.length > 0 && (
            <>
              <span aria-hidden="true">•</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTags(!showTags);
                }}
                className="text-gray-400 hover:text-gray-600 hover:underline"
              >
                {showTags ? "Hide Tags" : `Show Tags`}
              </button>
            </>
          )}
        </div>

        {showTags && (
          <div className="flex flex-wrap gap-2 mt-2">
            {tags.map((tag, idx) => (
              <span
                key={idx}
                className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

SearchResultCard.displayName = "SearchResultCard";

export default SearchResultCard;
